/**
 * Permission Selector Component
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useGetPermissionsQuery, useGetPermissionCategoriesQuery } from '../../../store/api/rbacApi';
import { palette } from '../../../theme/palette';
import { filterRetiredPermissionsFromList } from '../../../utils/rbacRetiredPermissions';

interface PermissionSelectorProps {
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
  /** When set, only permissions whose `code` is in this list are shown (e.g. employee custom perms limited to selected role). Omit for full catalog (role create/edit). */
  visiblePermissionCodes?: string[];
}

export const PermissionSelector = ({
  selectedPermissions,
  onChange,
  visiblePermissionCodes,
}: PermissionSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  const { data: categoriesData, isLoading: categoriesLoading } = useGetPermissionCategoriesQuery();
  const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery({
    page_size: 200,
  });

  const isLoading = categoriesLoading || permissionsLoading;

  const categories = categoriesData?.data || [];
  const permissions = filterRetiredPermissionsFromList(permissionsData?.results || []);

  const scopedPermissions = useMemo(() => {
    if (visiblePermissionCodes === undefined) return permissions;
    const allow = new Set(visiblePermissionCodes);
    return permissions.filter((p) => allow.has(p.code));
  }, [permissions, visiblePermissionCodes]);

  // Group permissions by category
  const permissionsByCategory = useMemo(() => {
    const grouped: Record<number, typeof scopedPermissions> = {};
    scopedPermissions.forEach((permission) => {
      if (!grouped[permission.category]) {
        grouped[permission.category] = [];
      }
      grouped[permission.category].push(permission);
    });
    return grouped;
  }, [scopedPermissions]);

  // Filter permissions by search
  const filteredPermissions = useMemo(() => {
    if (!search) return scopedPermissions;
    const searchLower = search.toLowerCase();
    return scopedPermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.code.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
    );
  }, [scopedPermissions, search]);

  const handleTogglePermission = (code: string) => {
    if (selectedPermissions.includes(code)) {
      onChange(selectedPermissions.filter((p) => p !== code));
    } else {
      onChange([...selectedPermissions, code]);
    }
  };

  const handleToggleCategory = (categoryId: number) => {
    const categoryPermissions = permissionsByCategory[categoryId] || [];
    const allSelected = categoryPermissions.every((p) => selectedPermissions.includes(p.code));

    if (allSelected) {
      // Deselect all
      onChange(
        selectedPermissions.filter(
          (code) => !categoryPermissions.some((p) => p.code === code)
        )
      );
    } else {
      // Select all
      const newCodes = categoryPermissions.map((p) => p.code);
      onChange([...new Set([...selectedPermissions, ...newCodes])]);
    }
  };

  const handleToggleAccordion = (categoryId: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!categories.length || !permissions.length) {
    return (
      <Alert severity="info">
        {t('rbac.permissions.noneAvailable')}
      </Alert>
    );
  }

  if (visiblePermissionCodes !== undefined && visiblePermissionCodes.length === 0) {
    return (
      <Alert severity="info">
        {t('rbac.permissions.roleNoPermissions')}
      </Alert>
    );
  }

  if (visiblePermissionCodes !== undefined && scopedPermissions.length === 0) {
    return (
      <Alert severity="warning">
        {t('rbac.permissions.noMatching')}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {t('rbac.permissions.heading', { count: selectedPermissions.length })}
      </Typography>

      <TextField
        placeholder={t('rbac.permissions.searchPlaceholder')}
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {categories
          .filter((category) => category.is_active)
          .sort((a, b) => a.display_order - b.display_order)
          .map((category) => {
            const categoryPerms = (permissionsByCategory[category.id] || []).filter((p) =>
              search
                ? filteredPermissions.some((fp) => fp.id === p.id)
                : true
            );

            // Hide categories with nothing to show (e.g. employee role scope: no perms in this category)
            if (categoryPerms.length === 0) return null;

            const selectedCount = categoryPerms.filter((p) =>
              selectedPermissions.includes(p.code)
            ).length;
            const allSelected = selectedCount === categoryPerms.length;
            const someSelected = selectedCount > 0 && !allSelected;

            return (
              <Accordion
                key={category.id}
                expanded={expandedCategories.includes(category.id)}
                onChange={() => handleToggleAccordion(category.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCategory(category.id);
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {category.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${selectedCount}/${categoryPerms.length}`}
                      size="small"
                      sx={{
                        backgroundColor: selectedCount > 0
                          ? (isDark ? palette.brand[800] : palette.brand[50])
                          : (isDark ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9'),
                        color: selectedCount > 0
                          ? (isDark ? palette.brand[200] : palette.brand[700])
                          : (isDark ? 'rgba(255, 255, 255, 0.5)' : '#64748B'),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 4 }}>
                    {categoryPerms
                      .sort((a, b) => a.display_order - b.display_order)
                      .map((permission) => (
                        <Box
                          key={permission.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            p: 1,
                            borderRadius: 1,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: isDark
                                ? 'rgba(59, 193, 207, 0.08)'
                                : '#F8FAFC',
                            },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedPermissions.includes(permission.code)}
                                onChange={() => handleTogglePermission(permission.code)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {permission.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {permission.description}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={permission.code}
                                    size="small"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 20,
                                      backgroundColor: isDark
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : '#F1F5F9',
                                      color: isDark
                                        ? 'rgba(255, 255, 255, 0.7)'
                                        : '#334155',
                                    }}
                                  />
                                </Box>
                              </Box>
                            }
                            sx={{ m: 0, flexGrow: 1 }}
                          />
                        </Box>
                      ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Box>
    </Box>
  );
};

